'use strict';

import TWEEN from '../../libs/tween.umd';
import { Vector3, MOUSE, TOUCH, Sphere } from 'three';
import {OrbitControls} from "../../libs/OrbitControls";
import { isDesktop, projectMode, threeJsVariables, UIRoot } from '../../settings';
import { isMobile, settingsData } from '../../settings';
import { animateCamera } from '../threejs/animation';
import {
    rotateCylinderFloorNumbers,
    showOrHideCylinderFloorNumbers,
} from '../components/cylinderFloorNumbers/cylinderFloorNumbers';
import { Compass } from '../components/compass/compass';
import {buildingsLabelResize, mobileBuildingLabelsHideLogic} from '../components/building-switch/buildingLabels';
import { cloudsPositionCorrection } from '../liveAnimations/clouds/clouds';
import {ViewMode} from "../../common";
import {createHtmlElement, getUrlParam, isPortraitMode} from '../../functions';
import { changeMenuStyle} from '../menu/menu';
import {poiIntersection} from '../poi/poiCss';
import { autoZoomIsActive, fitModelInViewport } from './zoomControl';
import { intro, OrbitToMap } from '../threejs/camera';
import { rotateHoverCursorWithCamera } from '../components/building-switch/buildingHover';
import { Disappear } from '../components/Disappear/Disappear';
import { Drizzle } from '../components/weather/Drizzle';
import { CylinderBase } from '../cylinderGrid/CylinderBase';
import { Favorite } from '../components/Favorite/Favorite';
import { PublicLayers } from '../components/publicLayers/PublicLayers';
import { DOF } from '../post-processing/Passes/DOF';
import { Environment } from '../components/Environment/Environment';
import { CameraCollision } from '../threejs/CameraCollision';
import { updateShadowMap } from '../threejs/lights';
import { BmbyMode } from '../mode/BmbyMode';
import { Cluster } from '../threejs/Cluster/CSS2DCluster';

let controlsBounceCount = 0;
let autoRotateTimer = null;
const autoRotateTime = 120000;

export const orbitControlsInit = () => {
    const target = config.threejs.orbitControls.target;
    const camera = threeJsVariables.perspectiveCamera;
    const renderer = threeJsVariables.renderer;
    const orbitControls = new OrbitControls( camera, renderer.domElement );
    orbitControls.enableDamping = true;
    orbitControls.dampingFactor = 0.1;
    orbitControls.autoRotateSpeed = 1;
    orbitControls.minDistance = 0;
    orbitControls.maxDistance = Infinity;
    // orbitControls.zoomSpeed = 3;
    orbitControls.enableZoom = false;
    orbitControls.maxPolarAngle = Math.PI * 0.4;
    orbitControls.enableKeys = true;
    orbitControls.enabled = false;
    orbitControls.target.set(target.x,target.y,target.z);
    orbitControls.target0.set(target.x,target.y,target.z);
    
    if (BmbyMode.active) {
        config.threejs.orbitControls.cylinder.desktop.min /= 1.3;
        config.threejs.orbitControls.cylinder.mobile.portrait.min /= 1.3;
        config.threejs.orbitControls.cylinder.mobile.landscape.min /= 1.3;
    }

    //remove bounce
    //orbitControls.callbackFunctions.onDolly = dollyOutBounce;

    // orbitControls.enabled = false;
    orbitControls.listenToKeyEvents( window );

    threeJsVariables.orbitControls = orbitControls;
    setOrbitControls(ViewMode.Orbit);
    setPolarAngle(ViewMode.Orbit);
    setPanLimits(ViewMode.Orbit);
    // debugOrbitControls();

    orbitControls.addEventListener('end', orbitControlsAfterChangeHandler);
    orbitControls.addEventListener('change',orbitControlsChangeHandler);

    return orbitControls;
};

export function autoRotateInit () {
    if (projectMode.LiteMode.isActive()) return;

    createHtmlElement(UIRoot,'','div',['swipe__onboarding','non-canvas', 'hide__onboarding']);
    // setLordIcon('onBoardingSwipe',swipeIcon,{
    //     trigger: 'hover',
    //     colors:'primary:#ffffff,secondary:#ffffff',
    //     style: 'width:125px;height:125px',
    //     stroke: 50,
    //     delay: 0
    // });

    autoRotateTimer = setTimeout(()=> autoRotate(true),isMobile ? 0 : 30000);
}

export function autoRotate(action) {
    if (settingsData.activeViewMode !== ViewMode.Orbit || getUrlParam('card')) {
        return;
    }

    const icon = document.querySelector('.swipe__onboarding');
    switch (action) {
        case true:
            if (icon) {
                icon.classList.remove('hide__onboarding');
                // icon.querySelector('.lord__icon').setAttribute('trigger','loop');
            }
            if (!isMobile) {
                threeJsVariables.orbitControls.autoRotate = true;
                PublicLayers.intersect();
                Environment.intersect();
            }
            break;
        case false:
            if (icon) {
                icon.classList.add('hide__onboarding');
                // icon.querySelector('.lord__icon').setAttribute('trigger','none');
            }

            if (threeJsVariables?.orbitControls)
                threeJsVariables.orbitControls.autoRotate = false;

            break;
    }
}

export function autoRotateTimerAction (action) {
    if (projectMode.LiteMode && projectMode.LiteMode?.isActive())
        return;

    switch (action) {
        case 'update':
            if (settingsData.activeViewMode === ViewMode.Orbit){
                clearTimeout(autoRotateTimer);
                autoRotate(false);
                autoRotateTimer = setTimeout(()=> autoRotate(true),autoRotateTime);
            }
            break;
        case 'cancel':
            clearTimeout(autoRotateTimer);
            autoRotate(false);
            break;
    }
}

window.addEventListener('click', () => {
    autoRotateTimerAction('cancel');
    autoRotateTimerAction('update');
});

export function debugOrbitControls () {
    if (!getUrlParam('dev')){
        return;
    }

    const {minDistance,maxDistance} = getOrbitControlsDistanceAndPan();

    const params = {
        scaleMobile: config.threejs.orbitControls.autoZoomSettings.mobile.portrait.scale,
        scaleDesktop: config.threejs.orbitControls.autoZoomSettings.desktop.scale,
        foreshorteningZoomCorrection: config.threejs.orbitControls.autoZoomSettings.desktop.foreshorteningZoomCorrection,
        minZoomCorrection: config.threejs.orbitControls.autoZoomSettings.desktop.minZoomCorrection,
        minAngle: threeJsVariables.orbitControls.minPolarAngle,
        maxAngle: threeJsVariables.orbitControls.maxPolarAngle,
        minPanX: threeJsVariables.orbitControls.minPan.x,
        minPanY: threeJsVariables.orbitControls.minPan.y,
        minPanZ: threeJsVariables.orbitControls.minPan.z,
        maxPanX: threeJsVariables.orbitControls.maxPan.x,
        maxPanY: threeJsVariables.orbitControls.maxPan.y,
        maxPanZ: threeJsVariables.orbitControls.maxPan.z,
        min: minDistance,
        max: maxDistance,
        'reload': reloadProject
    };

    // const gui = new GUI();
    const {gui} = threeJsVariables;
    gui.width = 300;

    const orbitControlsFolder = gui.addFolder('Orbit controls');

    const zoom = orbitControlsFolder.addFolder('Zoom');
    const autoZoom = zoom.addFolder('Auto');
    const manualZoom = zoom.addFolder('Manual');

    autoZoom.add(params,'scaleMobile',0,50).step( 0.01 ).onChange( function ( value ) {
        config.threejs.orbitControls.autoZoomSettings.mobile.portrait.scale = value;
    });
    autoZoom.add(params,'scaleDesktop',0,50).step( 0.01 ).onChange( function ( value ) {
        config.threejs.orbitControls.autoZoomSettings.desktop.scale = value;
    });

    autoZoom.add(params,'foreshorteningZoomCorrection', 0, 50).step(0.01).onChange( function ( value ) {
        config.threejs.orbitControls.autoZoomSettings.desktop.foreshorteningZoomCorrection = value;
    });

    autoZoom.add(params,'minZoomCorrection', 0, 50).step(0.01).onChange( function ( value ) {
        config.threejs.orbitControls.autoZoomSettings.desktop.minZoomCorrection = value;
    });

    manualZoom.add(params,'min', 0, 1000).step(0.01).onChange( function ( value ) {
        threeJsVariables.orbitControls.minDistance = value;
        threeJsVariables.trackballControls.minDistance = value;
    });

    manualZoom.add(params,'max', 0, 1000).step(0.01).onChange( function ( value ) {
        threeJsVariables.orbitControls.maxDistance = value;
        threeJsVariables.trackballControls.maxDistance = value;
    });


    const angle = orbitControlsFolder.addFolder('Polar angle');
    angle.add(params, 'minAngle',0,4).step(0.01).onChange( function ( value ) {
       threeJsVariables.orbitControls.minPolarAngle = value;
    });
    angle.add(params, 'maxAngle',0,4).step(0.01).onChange( function ( value ) {
        threeJsVariables.orbitControls.maxPolarAngle = value;
    });

    const pan = orbitControlsFolder.addFolder('Pan');
    pan.add(params,'minPanX', -1000, 1000).step(1).onChange( function ( value ) {
        threeJsVariables.orbitControls.minPan.setX(value);
    });
    pan.add(params,'minPanY', -1000, 1000).step(1).onChange( function ( value ) {
        threeJsVariables.orbitControls.minPan.setY(value);
    });
    pan.add(params,'minPanZ', -1000, 1000).step(1).onChange( function ( value ) {
        threeJsVariables.orbitControls.minPan.setZ(value);
    });
    pan.add(params,'maxPanX', -1000, 1000).step(1).onChange( function ( value ) {
        threeJsVariables.orbitControls.maxPan.setX(value);
    });
    pan.add(params,'maxPanY', -1000, 1000).step(1).onChange( function ( value ) {
        threeJsVariables.orbitControls.maxPan.setY(value);
    });
    pan.add(params,'maxPanZ', -1000, 1000).step(1).onChange( function ( value ) {
        threeJsVariables.orbitControls.maxPan.setZ(value);
    });

    orbitControlsFolder.add(params,'reload');


}

function reloadProject () {
    intro();
}

function updateShadowsAfterControlsChange () {
    if (config.threejs.orbitControls.updateShadows && !config.resources.bundle.dynamicShadows && settingsData.activeViewMode === ViewMode.Orbit) {
        updateShadowMap();
    }
}

function orbitControlsAfterChangeHandler () {
    updateShadowsAfterControlsChange();
   // flatLabelResize();
}

let _last_distance = 0;
let try_count = 0;
let moveToBorderMoment = 0;
let movesTimeGap = 0;
function orbitControlsChangeHandler (event) {
    if (settingsData.activeViewMode === ViewMode.Cylinder && !settingsData.destroyedMode) {
        rotateCylinderFloorNumbers();
    }

    createOrbitToMapButton();
    // compassRotation();
    Compass.getScope('orbit')?.rotate();
    rotateHoverCursorWithCamera();
    if (settingsData.activeViewMode === ViewMode.FloorPlan){
        CylinderBase.rotateTexts();
    } else if (settingsData.activeViewMode === ViewMode.Cylinder) {
        CylinderBase.rotateTexts();
        Favorite.intersect();
    } else {
        Cluster.update();
        const o = threeJsVariables.trackballControls;
        let dx = o.object.position.x - o.target.x;
        let dy = o.object.position.y - o.target.y;
        let dz = o.object.position.z - o.target.z;
        let d = dx*dx+dy*dy+dz*dz;
        let max = o.maxDistance*o.maxDistance*0.999;
        let max2= o.maxDistance*o.maxDistance*0.96;

        if (Environment.getActivePublicLayer()?.animateCamera || PublicLayers.activeLayer?.animateCamera) {
            // const maxDistance = getOrbitControlsDistanceAndPan(ViewMode.Orbit).maxDistance;
            max = 0;
            max2 = -Infinity;
        }

        // reset vars if user zoom in
        if (d < max2) {
            hideOrbitToMapButton();
            // try_count = 0;
            // moveToBorderMoment = 0;
        }
        // if we on the edge of the orbit
        if (d > _last_distance && d > max) {
            // try_count++;
            // movesTimeGap = Date.now() - moveToBorderMoment;
            // bounce camera out and show map button for the first try
            // if (try_count <= 1 || movesTimeGap < 1000) {
            //     !moveToBorderMoment && (moveToBorderMoment = Date.now());
                showOrbitToMapButton();
                // moveFromBorder();
            // go to 2DMap for the second try and delay
            // } else {
            //     hideOrbitToMapButton();
            //     try_count = 0;
            //     moveToBorderMoment = 0;
            //     OrbitToMap();
            // }
        }
        _last_distance = d;
    }

    if (threeJsVariables.orbitControls.enabled) {
        // toggle360Icons();
        showOrHideCylinderFloorNumbers();
        buildingsLabelResize();
        // flatLabelResize();
        //changeHeaderMode();
        changeMenuStyle();
        // renderUpdateValuesOnDemand();
        // updateLabels();
        // buildingSwitcherButtonsToggle();
        // showPoiInfo();
        mobileBuildingLabelsHideLogic();
        poiIntersection();
        Drizzle.rotateRainbow();
        DOF.updateFocus();
        CameraCollision.checkCollision();
        threeJsVariables.background?.collisionDetect();
    }

    Disappear.update();
    // rotateStreetNames();
    // updateRenderer();
    // renderUpdateValuesOnDemand(false);
    settingsData.needsRerender = true;


}

export function createOrbitToMapButton() {
    if (document.getElementsByClassName('orbit-to-map-button')[0] || isDesktop)
        return;
    const button = document.createElement('button');
    button.type = 'button';
    button.classList.add('orbit-to-map-button', 'control-button', 'hidden');
    
    if (threeJsVariables.orbitBuildingsNavigation.active) {
        button.classList.add('--orbit-buildings-navigation-exist');
    }
    
    // button.style.opacity = '0';
    // button.display = 'none';
    button.addEventListener('click', () => {
        moveToBorderMoment = 0;
        try_count = 0;
        OrbitToMap();
        hideOrbitToMapButton();
    });
    button.appendChild(getIcon());
    button.appendChild(getText());
    button.appendChild(getBackButton());
    UIRoot.appendChild(button);

    function getIcon() {
        const icon = document.createElement('img');
        icon.classList.add('icon');
        icon.src = "./img/icons/noun-map.svg";
        icon.setAttribute('aria-hidden', 'true');

        return icon;
    }

    function getText() {
        const text = document.createElement('span');
        text.classList.add('text', 'language-string');
        text.setAttribute('data-dictionary', 'map');
        text.innerText = 'map';

        return text;
    }

    function getBackButton() {
        const button = document.createElement('button');
        button.classList.add('back-button', 'icon-back', 'control-button', 'hidden');
        button.type = 'button';
        button.addEventListener('click', (e) => {
            e.stopPropagation();
            if (PublicLayers.isActive) {
                const layerType = PublicLayers.activeLayer?.type;
                const layer = document.querySelector('.public-layer__btn.poi') ||
                document.querySelector(`.public-layer__btn[data-public-layer="${layerType}"]`);
                layer && PublicLayers.scope.clickHandler({ target: layer, action: 'backButton' });
            }else if (Environment.isActive()) {
                const layerId = Environment.getActivePublicLayer()?.id;
                const layer = document.querySelector('.public-layer__btn.poi') ||
                document.querySelector(`.public-layer__btn[data-public-layer="${layerId}"]`);
                layer && Environment.scope.publicLayersHandler({ target: layer, action: 'backButton' });
            }
        });

        return button;
    }
}

export function showOrbitToMapButton() {
    if (settingsData.activeViewMode !== ViewMode.Orbit) return;

    const button = document.getElementsByClassName('orbit-to-map-button')[0];
    button?.classList.remove('hidden');
}

export function hideOrbitToMapButton() {
    // if (isMobile)
    //     return;

    const button = document.getElementsByClassName('orbit-to-map-button')[0];
    button?.classList.add('hidden');
}

export function showOrbitToMapBackButton() {
    if (settingsData.activeViewMode !== ViewMode.Orbit) return;

    const button = document.querySelector('.orbit-to-map-button .back-button');
    button.classList.remove('hidden');
}

export function hideOrbitToMapBackButton() {
    const button = document.querySelector('.orbit-to-map-button .back-button');
    button.classList.add('hidden');
}

// export function dollyOutBounce () {
//     if (settingsData.activeViewMode !== ViewMode.Orbit){
//         return;
//     }
//
//
//     // const controls = threeJsVariables.orbitControls;
//     // const currentPolarAngle = Number(controls.getPolarAngle().toFixed(3));
//     // const minPolarAngle = Number(controls.minPolarAngle.toFixed(3));
//     //
//     // if (controlsBounceCount === settingsData.globalSettings.totalDollyBounce){
//     //     openMap();
//     //     controlsBounceCount = 0;
//     //     return;
//     // }
//     //
//     // if (currentPolarAngle === minPolarAngle){
//     //     const v = new Vector3().copy(controls.object.position);
//     //     v.setLength(getCurrentCameraDistance() - 40);
//     //
//     //     gsap.to(controls.object.position,{duration: 1, x: v.x, y: v.y, z: v.z,
//     //         onStart:()=>{
//     //             controls.enabled = false;
//     //         },
//     //         onComplete:()=>{
//     //             controls.enabled = true;
//     //             controlsBounceCount++;
//     //         }});
//     // } else {
//     //     controlsBounceCount = 0;
//     // }
// }

// function openMap () {
//     const map = document.querySelector('.content__page[data-menu-item="map"]');
//     if (map.classList.contains('show-page')){
//         return;
//     }
//
//     gsap.to(map,{opacity: 1, visibility: 1, duration: 1,
//         onStart:()=>{
//             map.style.visibility = 'visible';
//         },onComplete:()=>{
//             openPage('map');
//             setMenuActive('map');
//             map.style.removeProperty('visibility');
//             map.style.removeProperty('opacity');
//         }});
// }

export function setPanLimits (viewMode = settingsData.activeViewMode) {
   const controls = threeJsVariables.orbitControls;

   const {minPan,maxPan,maxSphere} = getOrbitControlsDistanceAndPan(viewMode);

   const checkMinValue = Object.values(minPan).filter(value => value === undefined);
   const checkMaxValue = Object.values(maxPan).filter(value => value === undefined);

   if (maxSphere && Object.keys(maxSphere).length && viewMode === ViewMode.Orbit) {
       controls.maxSphere = new Sphere(new Vector3().fromArray(maxSphere.center),maxSphere.radius);
   } else {
       controls.maxSphere = undefined;
   }

   if (!checkMaxValue.length && !checkMinValue.length){
       const minValueVector = setVectorValueFromControls(minPan);
       const maxValueVector = setVectorValueFromControls(maxPan);

       const minVector = new Vector3(minValueVector.x,minValueVector.y,minValueVector.z);
       const maxVector = new Vector3(maxValueVector.x,maxValueVector.y,maxValueVector.z);
       controls.minPan = minVector;
       controls.maxPan = maxVector;
   } else {
       resetPanLimits();
   }
}

export function getOrbitControlsDistanceAndPan (viewMode = settingsData.activeViewMode) {
    const {activate,max,min} = config.threejs.orbitControls.autoZoomSettings;
    let settingsKey;
    let settings;
    // const isMobile = detectMobile();

    switch (isMobile) {
        case true:
            settingsKey = 'mobile';
            const isPortrait = isPortraitMode();
            if (isPortrait){
                settings = config.threejs.orbitControls[viewMode][settingsKey]['portrait'];
            } else {
                settings = config.threejs.orbitControls[viewMode][settingsKey]['landscape'];
            }
            break;
        case false:
            settingsKey = 'desktop';
            settings = config.threejs.orbitControls[viewMode][settingsKey];
        break;
    }



    return {
     minDistance: activate ? min : settings.min,
     maxDistance: activate ? max : settings.max,
     maxSphere: settings.maxSphere,
     maxPan: settings.maxPan,
     minPan: settings.minPan,
     position: settings.position,
     target: settings.target
    }
}

export function enableOrbitControls (action) {
    threeJsVariables.orbitControls.enabled = action;
    threeJsVariables.trackballControls.enabled = action;
}

export function enableTrackballControls(action) {
    threeJsVariables.trackballControls.enabled = action;
}

export function isOrbitControlsEnabled () {
    return threeJsVariables.orbitControls.enabled;
}

// export async function changeCanvasSizeWhenCardIsOpened (needOpen) {
//     return ;
//     const {innerWidth, innerHeight} = window;
//     let floorSliderWidth = 0;
//
//     const card = UnitCardClass.findCard()?.html;
//     const floorSlider = document.querySelector('.fs-slider-open');
//
//         if (needOpen){
//             if (card) {
//
//                 if (innerWidth < 1000 && innerWidth > innerHeight){  // mobile landscape
//
//                     if (floorSlider) {
//                         floorSliderWidth = floorSlider.getBoundingClientRect().width;
//                     } else {
//                         floorSliderWidth = 0;
//                     }
//
//                     const cardWidth = card.getBoundingClientRect().width;
//                     const newWidth = window.innerWidth - cardWidth - floorSliderWidth;
//                     const size = {
//                         innerWidth: newWidth,
//                         innerHeight: window.innerHeight,
//                     };
//
//                 } else if (innerHeight < 1000 && innerHeight > innerWidth && settingsData.buildingDestroyed){ // mobile portrait
//                     const cardHeight = card.getBoundingClientRect().height;
//                     const newHeight = window.innerHeight - cardHeight;
//                     const size = {
//                         innerWidth: window.innerWidth,
//                         innerHeight: newHeight,
//                     };
//                 }
//             }
//
//         }
// }

export function resetPanLimits () {
    const controls = threeJsVariables.orbitControls;
    controls.minPan = undefined;
    controls.maxPan = undefined;
    controls.maxSphere = undefined;
}

function setVectorValueFromControls (object) {
    const {orbitControlsVector} = getWorldData();
    const newData = new Vector3();
    Object.entries(object).forEach(([key,value]) =>{
        if (value === 0){
            newData[key] = orbitControlsVector[key];
        } else {
            newData[key] = value;
        }
    });

    return newData;
}

export async function setDefaultForeshortening () {
    const {cameraVector,orbitControlsVector} = getWorldData();
    await fitModelInViewport();
    const target = config.threejs.orbitControls.target;
    const {minAngle} = getPolarAngleFromViewMode(ViewMode.Orbit);
    const {minDistance,maxDistance} = getOrbitControlsDistanceAndPan(ViewMode.Orbit);
    const foreshortening = config.threejs.orbitControls.autoZoomSettings.foreshortening;

    let length;

    if (autoZoomIsActive()){
        length = foreshortening;
    } else {
        length = (maxDistance + minDistance) / 2;
    }

    const defaultTarget = new Vector3(target.x,target.y,target.z);

    const position = new Vector3().subVectors(cameraVector,orbitControlsVector).normalize().setLength(length).add(orbitControlsVector);

    // const animate = new Promise(resolve => {
        await animateCamera(
            cameraVector,
            position,
            orbitControlsVector,
            defaultTarget,
            undefined,
            minDistance,
            minAngle,
            0,
            1000,
            TWEEN.Easing.Sinusoidal.InOut,
            ()=>{
                // setSmoothDistance();
                setOrbitControls(ViewMode.Orbit);
                setPolarAngle(ViewMode.Orbit);
                // setMinAndMaxControlsDistance(ViewMode.Orbit);
                setPanLimits(ViewMode.Orbit);
            },
            ()=>{
                setMinAndMaxControlsDistance(ViewMode.Orbit);
                // resolve(true);
                // console.log(threeJsVariables.orbitControls.getAzimuthalAngle());
            });
    // });

    // settingsData.animationsList.push(animate);

}

export function getCenterDistance (viewMode = settingsData.activeViewMode) {
  const distanceAndPan = settingsData.globalSettings.orbitControlsDistanceAndPan[viewMode];
  const center = (distanceAndPan.max + distanceAndPan.min) / 2;

  return center;
}

// export function setSmoothDistance () {
//     return;
//     const { minDistance,maxDistance  } = getOrbitControlsDistanceAndPan();
//     const min = minDistance / 100;
//     const max = maxDistance * 100;
//     threeJsVariables.orbitControls.minDistance = min;
//     threeJsVariables.orbitControls.maxDistance = max;
//     threeJsVariables.trackballControls.minDistance = threeJsVariables.orbitControls.minDistance;
//     threeJsVariables.trackballControls.maxDistance = threeJsVariables.orbitControls.maxDistance;
// }

export function getDirectionAndDistance (modeView = settingsData.activeViewMode) {
    const currentDistance = Math.abs(threeJsVariables.perspectiveCamera.position.z);
    const cameraRoute = threeJsVariables.perspectiveCamera.position.z < 0 ? -1 : 1;
    const { minDistance } = getOrbitControlsDistanceAndPan(modeView);

    switch (modeView) {
        case ViewMode.Orbit:
            return minDistance * 1.4 * cameraRoute;
            // if (currentDistance > orbit.max){
            //     return ((orbit.max + orbit.min) / 2) * cameraRoute;
            // } else if (currentDistance < orbit.min){
            //     return orbit.min * cameraRoute;
            // } else {
            //     return currentDistance * cameraRoute;
            // }
        case ViewMode.Cylinder:
            return minDistance / 2 * cameraRoute;
            // if (currentDistance > cylinder.max){
            //     return cylinder.max * cameraRoute;
            // } else if (currentDistance < cylinder.min) {
            //     return ((cylinder.max + cylinder.min) / 2) * cameraRoute;
            // } else {
            //     return currentDistance * cameraRoute;
            // }
    }
}

export function setViewDeviceTargetAndPosition (viewMode = settingsData.activeViewMode) {
    const {target,position} = getOrbitControlsDistanceAndPan(viewMode);
    if (target) {
        threeJsVariables.orbitControls.target.set(
            target.x,
            target.y,
            target.z
        )
    }
    
    if (position) {
        threeJsVariables.perspectiveCamera.position.set(
            position.x,
            position.y,
            position.z
        )
    }
}


export function setOrbitControls (viewMode = settingsData.activeViewMode) {
    const controls = threeJsVariables.orbitControls;

    switch (viewMode) {
        case ViewMode.Orbit:
            controls.mouseButtons = {
                LEFT: MOUSE.ROTATE,
                MIDDLE: MOUSE.DOLLY,
                RIGHT: MOUSE.PAN
            };

            controls.touches = {
                ONE: TOUCH.ROTATE,
                TWO: TOUCH.DOLLY_PAN
            };

            controls.keys = {
                LEFT: 37,
                UP: 38,
                RIGHT: 39,
                BOTTOM: 40
            };
            controls.rotationAndPan = false;
            controls.screenSpacePanning = false;
            controls.enablePan = true;
            controls.enableSetAngle = false;

            break;
        case ViewMode.Cylinder:
            const control = cylinderControls();
            Object.entries(control).forEach(([key,value]) => {
               controls[key] = value;
            });

            // controls.mouseButtons = {
            //     LEFT: MOUSE.ROTATE,
            //     MIDDLE: THREE.MOUSE.DOLLY,
            //     RIGHT: null
            // };
            //
            // controls.touches = {
            //     ONE: THREE.TOUCH.ROTATE,
            //     TWO: THREE.TOUCH.DOLLY_PAN
            // };
            //
            // controls.rotationAndPan = true;
            // controls.screenSpacePanning = true;
            // controls.enablePan = true;
            // controls.enableSetAngle = false;

            break;
        case ViewMode.FloorPlan:
            controls.mouseButtons = {
                LEFT: MOUSE.ROTATE,
                MIDDLE: MOUSE.DOLLY,
                RIGHT: null
            };

            controls.touches = {
                ONE: TOUCH.ROTATE,
                TWO: TOUCH.DOLLY_PAN
            };

            controls.rotationAndPan = false;
            controls.screenSpacePanning = false;
            controls.enableSetAngle = false;
            break;
    }

}


function cylinderControls () {
    if (config.threejs.orbitControls.cylinder.controls === 'map') {
        return {
            mouseButtons: {
                LEFT: MOUSE.ROTATE,
                MIDDLE: MOUSE.DOLLY,
                RIGHT: MOUSE.PAN
            },
            touches: {
                ONE: TOUCH.ROTATE,
                TWO: TOUCH.DOLLY_PAN
            }
        }
    }

    if (config.features.privateHouseMode) {
        return {
            mouseButtons: {
                LEFT: MOUSE.ROTATE,
                MIDDLE: MOUSE.DOLLY,
                RIGHT: MOUSE.PAN
            },
            touches: {
            ONE: TOUCH.ROTATE,
            TWO: TOUCH.DOLLY_PAN
            },
            keys: {
                LEFT: 37,
                UP: 38,
                RIGHT: 39,
                BOTTOM: 40
            },
            rotationAndPan: false,
            screenSpacePanning: false,
            enablePan: true,
            enableSetAngle: false
        }
    }


    return {
        mouseButtons : {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: null
        },
        touches: {
            ONE: TOUCH.ROTATE,
            TWO: TOUCH.DOLLY_PAN
        },
        rotationAndPan : true,
        screenSpacePanning: true,
        enablePan: true,
        enableSetAngle: false
    }
}


export function rotateToCamera (diff = 0) {
    return threeJsVariables.orbitControls.getAzimuthalAngle() + Math.PI / 2 + diff;
}

export function freezeCurrentDistance () {
    const currentDistance = getCurrentCameraDistance();
    threeJsVariables.orbitControls.minDistance = currentDistance;
    threeJsVariables.orbitControls.maxDistance = currentDistance;
}

export function setMinAndMaxControlsDistance (modeView = settingsData.activeViewMode) {
    // if (Vantage.isActive()) return;
    const {activate,min,max} = config.threejs.orbitControls.autoZoomSettings;
    const { minDistance,maxDistance  } = getOrbitControlsDistanceAndPan(modeView);

    // threeJsVariables.orbitControls.minDistance =  activate ? min : minDistance;
    // threeJsVariables.orbitControls.maxDistance = activate ? max : maxDistance;
    // threeJsVariables.trackballControls.minDistance = threeJsVariables.orbitControls.minDistance;
    // threeJsVariables.trackballControls.maxDistance = threeJsVariables.orbitControls.maxDistance;

    threeJsVariables.trackballControls.minDistance = activate ? min : minDistance;
    threeJsVariables.trackballControls.maxDistance = activate ? max : maxDistance;
}

export function setMaxAndMinDistanceFromViewport (distance,viewMode = settingsData.activeViewMode) {
    const mode = config.threejs.orbitControls[viewMode];
    if (mode){
        switch (viewMode) {
            case ViewMode.Orbit:
                mode.min = isMobile ?  distance / 1.3 : distance / 1.2;
                mode.max =  isMobile ? distance * 1.5 : distance * 2.5;
                break;
            case ViewMode.Cylinder:
                mode.min = distance / 1.4;
                mode.max = distance * 1.2;
                break;
        }
        setMinAndMaxControlsDistance(viewMode);
    }
    cloudsPositionCorrection();
}

export function setPolarAngle (viewMode = settingsData.activeViewMode) {
    const controls = threeJsVariables.orbitControls;
    const orbitPolarAngle = getPolarAngleFromViewMode(viewMode);
    controls.maxPolarAngle = orbitPolarAngle.maxAngle;
    controls.minPolarAngle = orbitPolarAngle.minAngle;
}

export function getWorldData (flat) {
    let flatWorldPosition,floorWorldPosition = null;
    if (flat){
        flatWorldPosition = flat.getWorldPosition(new Vector3());
        floorWorldPosition = flat.parent?.getWorldPosition(new Vector3()) || new Vector3();
    }

    const cameraWorldPosition = threeJsVariables.perspectiveCamera.getWorldPosition(new Vector3());
    const orbitControlsTarget = threeJsVariables.orbitControls.target;

    return{
        cameraVector : cameraWorldPosition,
        orbitControlsVector: orbitControlsTarget,
        flatVector: flatWorldPosition,
        floorVector: floorWorldPosition
    }
}

export function getCurrentCameraDistance () {

    return threeJsVariables.perspectiveCamera.position.distanceTo(threeJsVariables.orbitControls.target);
}

window.cam = ()=>{
  const dist = getCurrentCameraDistance();
  console.log(dist);
};

export function getOrbitControlsDistanceAndAngle () {
    return{
        currentMinDistance: threeJsVariables.trackballControls.minDistance,
        currentMaxDistance: threeJsVariables.trackballControls.maxDistance,
        currentMinAngle: threeJsVariables.orbitControls.minPolarAngle,
        currentMaxAngle: threeJsVariables.orbitControls.maxPolarAngle
    };
}

export function getOrbitControlsCurrentPolarAngle () {
    return threeJsVariables.orbitControls.getPolarAngle();
}

export function getPolarAngleFromViewMode (viewMode = settingsData.activeViewMode) {
    if (config.features.privateHouseMode) {
        return {
            minAngle: config.threejs.orbitControls.orbit.polarAngle.min,
            maxAngle: config.threejs.orbitControls.orbit.polarAngle.max
        }
    }
    switch (viewMode) {
        case ViewMode.Orbit:
            return{
                // minAngle: 0.48447792903702325,
                // maxAngle: Math.PI * 0.45
                minAngle: config.threejs.orbitControls.orbit.polarAngle.min,
                maxAngle: config.threejs.orbitControls.orbit.polarAngle.max
            };
        case ViewMode.Cylinder:
            return {
                // minAngle: Math.PI * 0.45,
                // maxAngle: 0.48447792903702325,
                minAngle: config.threejs.orbitControls.cylinder.polarAngle.min,
                maxAngle: config.threejs.orbitControls.cylinder.polarAngle.max,
            };
        case ViewMode.FloorPlan:
            return {
                // minAngle: 0.48447792903702325,
                // maxAngle: Math.PI * 0.30
                minAngle: config.threejs.orbitControls.floorplan.polarAngle.min,
                maxAngle: config.threejs.orbitControls.floorplan.polarAngle.max
            };
    }
}
